/**
 * vd-guide-bubble — mini-carte video flottante contextuelle.
 *
 * UX :
 * - Etat reduit : vignette poster + titre du guide de la PAGE COURANTE.
 * - Souris : le survol AGRANDIT la carte (scale) et lance une preview muette ;
 *   le clic ouvre le modal complet.
 * - Tactile (hover inexistant sur iOS/Android) : 1er tap = agrandir,
 *   2e tap = ouvrir le modal. Un ✕ masque le guide (memorise).
 * - Pastille "pulse" tant que le guide n'a pas ete vu.
 * - Positionnee a GAUCHE de la pile de FAB existante (softphone + IA) pour ne
 *   jamais la chevaucher (lecon incident bulle softphone).
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import { GuideService } from '../../services/guide.service';
import { GuideModalComponent } from './guide-modal.component';

@Component({
  selector: 'vd-guide-bubble',
  standalone: true,
  imports: [CommonModule, TranslocoModule, GuideModalComponent],
  templateUrl: './guide-bubble.component.html',
  styleUrls: ['./guide-bubble.component.css'],
})
export class GuideBubbleComponent {
  readonly guide = inject(GuideService);

  readonly expanded = signal(false);   // etat tactile "agrandi"
  readonly modalOpen = signal(false);
  readonly hovering = signal(false);

  @ViewChild('previewVideo') previewVideo?: ElementRef<HTMLVideoElement>;

  readonly visible = computed(() =>
    !!this.guide.activeGuide()
    && !!this.guide.activeVariant()
    && !this.guide.activeDismissed()
    && !/^\/ai-agents\/(marketer|right-hand|accountant)(\/|$|\?)/.test(
      (this.guide.currentUrl() || '').split('?')[0] || '',
    ),
  );

  readonly touchDevice = typeof window !== 'undefined'
    && window.matchMedia?.('(hover: none)').matches === true;

  onEnter(): void {
    if (this.touchDevice) return;
    this.hovering.set(true);
    const v = this.previewVideo?.nativeElement;
    if (v) {
      v.muted = true;
      // preload="none" : la source n'est chargee qu'au 1er survol.
      if (v.readyState === 0) v.load();
      v.currentTime = 0;
      v.play().catch(() => void 0);
    }
  }

  onLeave(): void {
    if (this.touchDevice) return;
    this.hovering.set(false);
    const v = this.previewVideo?.nativeElement;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  }

  onCardClick(): void {
    if (this.touchDevice && !this.expanded()) {
      // 1er tap doigt = agrandir (equivalent du hover souris).
      this.expanded.set(true);
      const v = this.previewVideo?.nativeElement;
      if (v) { v.muted = true; v.play().catch(() => void 0); }
      return;
    }
    this.openModal();
  }

  openModal(): void {
    this.expanded.set(false);
    this.hovering.set(false);
    this.previewVideo?.nativeElement?.pause();
    this.modalOpen.set(true);
  }

  collapse(ev?: Event): void {
    ev?.stopPropagation();
    this.expanded.set(false);
    this.previewVideo?.nativeElement?.pause();
  }

  dismiss(ev: Event): void {
    ev.stopPropagation();
    const g = this.guide.activeGuide();
    if (g) this.guide.dismiss(g.id);
    this.expanded.set(false);
  }

  onModalClosed(watched: boolean): void {
    this.modalOpen.set(false);
    const g = this.guide.activeGuide();
    if (watched && g) this.guide.markSeen(g.id);
  }
}
